/**
 * @description
 * Parser for LTI TruCAM / TruCAM II laser speed gun exports.
 *
 * The device exports one JPEG plus one metadata .txt per violation. The .txt is UTF-16LE with
 * a BOM and CRLF lines (decoding happens before the parser - see decode_upload.js); the content
 * is `Key: Value` lines:
 *
 *   Serial Number: TC000012
 *   Firmware: 2.8.12  100.200.1.18
 *   Date: 29/10/2019            ← DD/MM/YYYY, device-local time
 *   Time: 10:14:14
 *   Frame Rate: 24
 *   Operator ID: 296879         ← officer's employee number
 *   Operator Name: ...
 *   Street Name: GPS            ← free text, or the literal "GPS"/"gps" when unset
 *   Street Code: 0
 *   Last Aligned: 17/10/2019  10:28:16
 *   Type: Speed
 *   Measured Speed: 147 km/h    ← the offense speed (laser measurement)
 *   Measured Distance: 290.5 m
 *   Speed Limit: 110 km/h
 *   Capture Speed: 143 km/h     ← speed at photo capture; absent on some firmware
 *   Latitude: 48° 18' 27.01" N  ← DMS
 *   Longitude: 17° 33' 21.96" E
 *
 * Not every value maps: license plate is never in the file (the operator reads it off the
 * photo), and `Capture Speed`/`Measured Distance` stay informational in the displayed text.
 * The serial number is returned separately so the import can refuse a file that belongs to a
 * different device than the operator picked.
 */

const RE_DMS = /(\d+)°\s*(\d+)'\s*([\d.]+)"\s*([NSEW])/

/** DMS string → signed decimal degrees, or null when the shape does not match. */
function dmsToDecimal(value) {
  const match = RE_DMS.exec(String(value ?? ''))
  if(!match) return null
  const degrees = Number(match[1]) + Number(match[2]) / 60 + Number(match[3]) / 3600
  const sign = (match[4] === 'S' || match[4] === 'W') ? -1 : 1
  return sign * Number(degrees.toFixed(7))
} // dmsToDecimal

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * Parses one TruCAM metadata file into workflow fields.
 * @param {string} txt - decoded metadata file content
 * @returns {{fields: object, warnings: Array<string>, serial: string}}
 */
// warnings are stored as translation templates - the GUI runs them through LANG.translate,
// which resolves each {{key}} part and keeps the dynamic detail after the colon verbatim
const W = (key)=>`{{wf.sktzrecords.warning.${key}}}`

function parse(txt) {
  const fields = {}
  const warnings = []
  const raw = {}

  for(const line of String(txt ?? '').replace(/^\uFEFF/, '').split(/\r?\n/)) {
    const trimmed = line.trim()
    if(!trimmed) continue
    const colon = trimmed.indexOf(':')
    if(colon <= 0) {
      warnings.push(`${W('UnparsedLine')}: ${trimmed}`)
      continue
    }
    const key = trimmed.slice(0, colon).trim().toLowerCase()
    const value = trimmed.slice(colon + 1).trim()
    if(!(key in raw)) raw[key] = value
  }

  const serial = String(raw['serial number'] || '').replace(/\s+/g, '')
  if(!serial) warnings.push(W('NoSerial'))

  // passage time: DD/MM/YYYY + HH:MM:SS in the device's local time
  const date = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw.date || '')
  const time = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(raw.time || '')
  if(date && time) {
    const stamp = new Date(
      Number(date[3]), Number(date[2]) - 1, Number(date[1]),
      Number(time[1]), Number(time[2]), Number(time[3]),
    ).getTime()
    if(Number.isFinite(stamp)) fields.event_dt = stamp * 1000
  }
  if(!('event_dt' in fields)) warnings.push(`${W('BadEventDt')}: Date=${raw.date || '(none)'} Time=${raw.time || '(none)'}`)

  const kmh = (key) => {
    if(!(key in raw)) return null
    // A negative sign expresses direction (vehicle receding from the gun), not negative speed.
    const match = /^(-?[\d.]+)\s*km\/h$/.exec(raw[key])
    if(!match) {
      warnings.push(`${W('NotKmh')}: ${key} = ${raw[key]}`)
      return null
    }
    return Number(match[1])
  }
  const speed = kmh('measured speed')
  if(speed !== null) fields.speed_kmh = Math.abs(speed)
  else if(!('measured speed' in raw)) warnings.push(W('NoSpeed'))
  const limit = kmh('speed limit')
  if(limit !== null) fields.speed_limit = limit

  const lat = dmsToDecimal(raw.latitude)
  const lon = dmsToDecimal(raw.longitude)
  if(lat !== null && lon !== null) {
    fields.gps_lat = lat
    fields.gps_lon = lon
  }else{
    warnings.push(`${W('BadGps')}: Latitude=${raw.latitude || '(none)'} Longitude=${raw.longitude || '(none)'}`)
  }

  if(raw['operator id']) fields.employee_num = raw['operator id']

  // "GPS"/"gps" is the device's placeholder for "position comes from GPS", not a street name
  const street = String(raw['street name'] || '').trim()
  if(street && !/^gps$/i.test(street)) fields.locality = street

  // the device's record type → IS TM violation code; TruCAM is a speed gun, anything else
  // in that field is unexpected and left for the operator to classify
  if(/^speed$/i.test(raw.type || '')) {
    fields.type = 'SPD'
  }else if(raw.type) {
    warnings.push(`${W('NotSpeedType')}: ${raw.type}`)
  }

  return { fields, warnings, serial }
} // parse

module.exports = { parse, dmsToDecimal }
